import React, { useEffect, useRef, useState } from "react";
import { LUDO_HOME, LUDO_N, LUDO_PATH, LUDO_SAFE, LUDO_YARD, Pt, ludoCell, ludoRot, ludoTrail, ludoViewTurns } from "@samra/shared";
import type { BoardProps } from "../pages/room";
import { t } from "../i18n";
import { Button, cx } from "../ui";
import { SEAT_COLORS } from "./colors";
import { soundOn, steps } from "../sound";

/*
 * Ludo board. Geometry comes from @samra/shared (the same file the iPhone app uses). This file only turns the
 * board so YOUR yard is at the bottom-left (a rotation, never a mirror) and draws it. Tokens move clockwise.
 */

const HOP_MS = 110;
const C = LUDO_N / 2;

export function Die({ value, rolling }: { value: number | null; rolling?: boolean }) {
  const pips: Record<number, [number, number][]> = {
    1: [[2, 2]], 2: [[1, 1], [3, 3]], 3: [[1, 1], [2, 2], [3, 3]], 4: [[1, 1], [3, 1], [1, 3], [3, 3]],
    5: [[1, 1], [3, 1], [2, 2], [1, 3], [3, 3]], 6: [[1, 1], [3, 1], [1, 2], [3, 2], [1, 3], [3, 3]],
  };
  return (
    <svg className={cx("die", rolling && "is-rolling")} viewBox="0 0 4 4" width="56" height="56" aria-label={value ? String(value) : ""}>
      <rect x="0.15" y="0.15" width="3.7" height="3.7" rx="0.7" fill="#FFF4E0" />
      {(value ? pips[value] : []).map(([x, y], i) => <circle key={i} cx={x} cy={y} r="0.36" fill="#1A1240" />)}
    </svg>
  );
}

const reducedMotion = () => typeof window !== "undefined" && !!window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches;

export function LudoBoard({ view, seat, names, myTurn, move, events, busy }: BoardProps) {
  const [lastRoll, setLastRoll] = useState<number | null>(null);
  const [rolling, setRolling] = useState(false);
  // Display-only hop animation: the real position is already in `view`; this just walks the token there.
  const [hop, setHop] = useState<{ key: string; pts: Pt[]; i: number } | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const me = seat >= 0 && seat < view.n;
  const myQ = me ? view.quadrant[seat] : 3;
  const turns = ludoViewTurns(myQ);
  const R = (p: Pt) => ludoRot(p, turns);

  useEffect(() => {
    const r = events.list.find((e: any) => e.type === "rolled");
    if (r) {
      setRolling(true);
      setLastRoll(r.value);
      const id = setTimeout(() => setRolling(false), 450);
      return () => clearTimeout(id);
    }
  }, [events.seq]);

  useEffect(() => {
    const mv = events.list.find((e: any) => e.type === "moved");
    if (!mv) return;
    const pts = ludoTrail(view.quadrant[mv.seat], mv.from, mv.to, mv.token);
    const n = pts.length - 1;
    if (timer.current) clearInterval(timer.current);
    if (reducedMotion() || n < 1) {
      setHop(null);
      if (soundOn()) steps(1, 0);
      return;
    }
    if (soundOn()) steps(n, HOP_MS);
    const key = `${mv.seat}-${mv.token}`;
    setHop({ key, pts, i: 0 });
    let i = 0;
    timer.current = setInterval(() => {
      i++;
      if (i >= n) {
        if (timer.current) clearInterval(timer.current);
        timer.current = null;
        setHop(null);
      } else setHop({ key, pts, i });
    }, HOP_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events.seq]);

  const quadOwner: Record<number, number> = {};
  view.quadrant.forEach((q: number, s: number) => (quadOwner[q] = s));
  const colorOfQ = (q: number) => (quadOwner[q] === undefined ? null : SEAT_COLORS[quadOwner[q]]);
  const legal: number[] = myTurn && view.phase === "move" && !busy ? view.legal : [];

  // Token positions (canonical frame), stacked when several share a cell.
  const placed: { s: number; i: number; x: number; y: number; p: number }[] = [];
  view.tokens.forEach((arr: number[], s: number) =>
    arr.forEach((p, i) => {
      const key = `${s}-${i}`;
      const at = hop && hop.key === key ? hop.pts[hop.i] : ludoCell(view.quadrant[s], p, i);
      const [x, y] = R(at);
      placed.push({ s, i, x, y, p });
    }),
  );
  const count = new Map<string, number>();
  const offset = (x: number, y: number) => {
    const k = `${x.toFixed(2)},${y.toFixed(2)}`;
    const n = count.get(k) ?? 0;
    count.set(k, n + 1);
    return n;
  };

  // A 1×1 cell given by its top-left corner in the canonical frame, drawn rotated.
  const cell = (x: number, y: number) => {
    const [cx2, cy2] = R([x + 0.5, y + 0.5]);
    return { x: cx2 - 0.45, y: cy2 - 0.45 };
  };
  const startArrow = (q: number) => {
    const a = R([LUDO_PATH[q * 13][0] + 0.5, LUDO_PATH[q * 13][1] + 0.5]);
    const b = R([LUDO_PATH[q * 13 + 1][0] + 0.5, LUDO_PATH[q * 13 + 1][1] + 0.5]);
    return (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;
  };
  const tri = (pts: Pt[]) => `M${pts.map((p) => R(p).map((v) => v.toFixed(3)).join(" ")).join("L")}Z`;
  const turnQ = view.quadrant[view.turn];

  return (
    <div className="ludo" data-seat={seat} data-turn={view.turn} data-quadrant={myQ}>
      <svg viewBox={`0 0 ${LUDO_N} ${LUDO_N}`} className="ludo-board" role="group" aria-label={`${t.game.ludo} — ${t.ludoDirection}`}>
        <rect width={LUDO_N} height={LUDO_N} rx="0.6" fill="#241a5c" />

        {/* yards, each with its owner's name, upright for every viewer */}
        {[0, 1, 2, 3].map((q) => {
          const [x, y] = LUDO_YARD[q];
          const [cx2, cy2] = R([x + 3, y + 3]);
          const s = quadOwner[q];
          const c = colorOfQ(q) ?? "#3a2f7a";
          const isTurn = s !== undefined && s === view.turn && !view.over;
          return (
            <g key={q} className={cx("ludo-yard", isTurn && "is-turn")}>
              <rect x={cx2 - 2.8} y={cy2 - 2.8} width="5.6" height="5.6" rx="0.8" fill={c} opacity={s === undefined ? 0.4 : 0.9} stroke={isTurn ? "#FFF4E0" : "none"} strokeWidth="0.14" />
              <rect x={cx2 - 2} y={cy2 - 2} width="4" height="4" rx="0.6" fill="#1A1240" opacity="0.55" />
              {[0, 1, 2, 3].map((i) => {
                const [hx, hy] = R(ludoCell(q, -1, i));
                return <circle key={i} cx={hx} cy={hy} r="0.42" fill="#120b33" opacity="0.6" />;
              })}
              {s !== undefined && (
                <text x={cx2} y={cy2 + (cy2 > C ? 2.48 : -2.18)} fontSize="0.5" textAnchor="middle" fill="#FFF4E0" fontWeight="700" className="ludo-name">
                  {s === seat ? t.ludoYouHere : (names[s] ?? "").slice(0, 14)}
                </text>
              )}
            </g>
          );
        })}

        {/* track */}
        {LUDO_PATH.map(([x, y], i) => {
          const startQ = i % 13 === 0 ? i / 13 : -1;
          const own = startQ >= 0 ? colorOfQ(startQ) : null;
          const r = cell(x, y);
          const [mx, my] = R([x + 0.5, y + 0.5]);
          return (
            <g key={i}>
              <rect x={r.x} y={r.y} width="0.9" height="0.9" rx="0.15" fill={own ?? "#FFF4E0"} opacity={0.94} />
              {startQ >= 0 && own && (
                <path d="M-0.17 -0.2 L0.13 0 L-0.17 0.2" transform={`translate(${mx} ${my}) rotate(${startArrow(startQ)})`} fill="none" stroke="#1A1240" strokeWidth="0.09" strokeLinecap="round" strokeLinejoin="round" />
              )}
              {LUDO_SAFE.has(i) && startQ < 0 && (
                <path d="M0 -0.3l0.09 0.2 0.21 0.03-0.15 0.15 0.04 0.21-0.19-0.1-0.19 0.1 0.04-0.21-0.15-0.15 0.21-0.03z" transform={`translate(${mx} ${my})`} fill="#1A1240" opacity="0.8" />
              )}
            </g>
          );
        })}

        {/* home columns and the centre */}
        {[0, 1, 2, 3].map((q) =>
          LUDO_HOME[q].map(([x, y], i) => {
            const r = cell(x, y);
            return <rect key={`${q}${i}`} x={r.x} y={r.y} width="0.9" height="0.9" rx="0.15" fill={colorOfQ(q) ?? "#3a2f7a"} opacity="0.78" />;
          }),
        )}
        <path d={tri([[6, 6], [9, 6], [7.5, 7.5]])} fill={colorOfQ(1) ?? "#3a2f7a"} />
        <path d={tri([[9, 6], [9, 9], [7.5, 7.5]])} fill={colorOfQ(2) ?? "#3a2f7a"} />
        <path d={tri([[9, 9], [6, 9], [7.5, 7.5]])} fill={colorOfQ(3) ?? "#3a2f7a"} />
        <path d={tri([[6, 9], [6, 6], [7.5, 7.5]])} fill={colorOfQ(0) ?? "#3a2f7a"} />
        {!view.over && turnQ !== undefined && (() => {
          const [tx, ty] = R([LUDO_YARD[turnQ][0] + 3, LUDO_YARD[turnQ][1] + 3]);
          const ang = (Math.atan2(ty - C, tx - C) * 180) / Math.PI;
          return <path d="M0.55 -0.28 L1.2 0 L0.55 0.28 Z" transform={`translate(${C} ${C}) rotate(${ang})`} fill="#FFF4E0" className="ludo-turn-arrow" />;
        })()}

        {/* tokens */}
        {placed.map(({ s, i, x, y, p }) => {
          const k = offset(x, y);
          const dx = k === 0 ? 0 : k % 2 ? 0.18 : -0.18;
          const dy = k === 0 ? 0 : k > 1 ? 0.18 : -0.18;
          const canMove = s === seat && legal.includes(i);
          const doMove = () => move({ type: "move", token: i });
          return (
            <g
              key={`${s}-${i}`}
              className={cx("token", canMove && "is-legal")}
              style={{ transform: `translate(${x + dx}px, ${y + dy}px)` }}
              onClick={canMove ? doMove : undefined}
              role={canMove ? "button" : undefined}
              tabIndex={canMove ? 0 : -1}
              aria-label={canMove ? `${t.pickMarble} ${i + 1}` : undefined}
              onKeyDown={canMove ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), doMove()) : undefined}
              data-token={`${s}-${i}`}
              data-pos={p}
            >
              {canMove && <circle r="0.5" fill="none" stroke="#FFF4E0" strokeWidth="0.09" className="pulse" />}
              <circle r="0.36" fill="#0b0622" opacity="0.4" transform="translate(0.05 0.07)" />
              <circle r="0.34" fill={SEAT_COLORS[s]} stroke="#1A1240" strokeWidth="0.08" />
              <circle r="0.12" cy="-0.06" fill="#FFF4E0" opacity="0.6" />
            </g>
          );
        })}
      </svg>
      <div className="board-controls">
        <Die value={lastRoll} rolling={rolling} />
        {myTurn && view.phase === "roll" && <Button onClick={() => move({ type: "roll" })} disabled={busy} autoFocus>{t.roll}</Button>}
        {myTurn && view.phase === "move" && <span className="hint">{t.pickMarble}</span>}
        {!myTurn && <span className="hint">{t.turnOf(names[view.turn] ?? "")}</span>}
      </div>
    </div>
  );
}
